export type DistributionMode = "free" | "paid";

export interface CommercialOffer {
  schema_version: "1.0.0";
  offer_id: string;
  listing_id: string;
  asset_id: string;
  asset_version: string;
  distribution_mode: DistributionMode;
  currency: string;
  price_minor: number;
  status: "active" | "inactive";
}

export interface OfferValidationResult {
  ok: boolean;
  errors: string[];
  value?: CommercialOffer;
}

const ID = /^[A-Za-z0-9._-]{3,180}$/;
const ASSET_ID = /^ast_[A-Za-z0-9][A-Za-z0-9_-]{7,127}$/;
const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;
const CURRENCY = /^[A-Z]{3}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function validateCommercialOffer(input: unknown): OfferValidationResult {
  if (!isRecord(input)) return { ok: false, errors: ["commercial offer must be an object"] };
  const x = input as Record<string, unknown>;
  const errors: string[] = [];

  if (x.schema_version !== "1.0.0") errors.push("schema_version must be 1.0.0");
  if (typeof x.offer_id !== "string" || !ID.test(x.offer_id)) errors.push("offer_id is invalid");
  if (typeof x.listing_id !== "string" || !ID.test(x.listing_id)) errors.push("listing_id is invalid");
  if (typeof x.asset_id !== "string" || !ASSET_ID.test(x.asset_id)) errors.push("asset_id is invalid");
  if (typeof x.asset_version !== "string" || !SEMVER.test(x.asset_version)) errors.push("asset_version must be semantic versioning");
  if (x.distribution_mode !== "free" && x.distribution_mode !== "paid") errors.push("distribution_mode must be free or paid");
  if (typeof x.currency !== "string" || !CURRENCY.test(x.currency)) errors.push("currency must be a three-letter uppercase code");
  if (!Number.isSafeInteger(x.price_minor) || Number(x.price_minor) < 0) errors.push("price_minor must be a non-negative safe integer");
  if (x.distribution_mode === "free" && x.price_minor !== 0) errors.push("free distribution requires price_minor 0");
  if (x.distribution_mode === "paid" && !(Number.isSafeInteger(x.price_minor) && Number(x.price_minor) > 0)) errors.push("paid distribution requires price_minor greater than 0");
  if (x.status !== "active" && x.status !== "inactive") errors.push("status must be active or inactive");

  return errors.length ? { ok: false, errors } : { ok: true, errors: [], value: input as unknown as CommercialOffer };
}

export function currencyMinorUnitDigits(currency: string): number {
  if (!CURRENCY.test(currency)) throw new Error("currency must be a three-letter uppercase code");
  const digits = new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits;
  if (typeof digits !== "number" || !Number.isInteger(digits) || digits < 0 || digits > 3) throw new Error(`unsupported currency minor-unit scale: ${currency}`);
  return digits;
}

export function minorToMajor(priceMinor: number, currency: string): number {
  if (!Number.isSafeInteger(priceMinor) || priceMinor < 0) throw new Error("price_minor must be a non-negative safe integer");
  return priceMinor / (10 ** currencyMinorUnitDigits(currency));
}

export function formatMinorMoney(priceMinor: number, currency: string, locale?: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minorToMajor(priceMinor, currency));
}
